import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { api, errorMessage, newIdempotencyKey, serverNow, unwrap, type DefaultPoiCategory, type EventData, type EventHeader, type EventStatus } from "../../api/client";
import { useSession } from "../../auth/session";
import { formatDateTime, fromParisInput } from "../../domain/format";
import { compareEvents, matchesName, phase, type EventPhase } from "../../domain/phase";
import { fr } from "../../i18n/fr";
import { Dialog, Field, PhaseBadge, StatusBadge, useOffline } from "../../ui/components";
import { LOCAL_ID, saveLocalEvent } from "./localEvent";

export function EventsPage() {
  const s = useSession();
  const [events, setEvents] = useState<EventHeader[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<EventStatus | "ALL">("ALL");
  const [phaseFilter, setPhaseFilter] = useState<EventPhase | "ALL">("ALL");
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const navigate = useNavigate();

  const load = () => api.GET("/events").then((r) => setEvents(unwrap(r))).catch((e) => setError(errorMessage(e, fr.errors)));
  useEffect(() => { void load(); }, []);

  const openFile = async (f: File) => {
    setFileErrors([]);
    let e: EventData;
    try { e = JSON.parse(await f.text()) as EventData; } catch (x) { setFileErrors([String(x)]); return; }
    try {
      const errs = unwrap(await api.POST("/sandbox/validate", { body: e }));
      if (errs.length > 0) { setFileErrors(errs); return; }
    } catch (x) { setFileErrors([errorMessage(x, fr.errors)]); return; }
    saveLocalEvent(e);
    navigate(`/test?event=${LOCAL_ID}`);
  };

  // SW-EV-01, SW-EV-02: filters on status and phase, search by name, ongoing events first
  const now = serverNow();
  const shown = (events ?? []).filter((e) => (filter === "ALL" || e.status === filter) && (phaseFilter === "ALL" || phase(e, now) === phaseFilter) && matchesName(e.name, query))
    .sort((a, b) => compareEvents(a, b, now));

  return (
    <div className="page">
      <div className="row between wrap">
        <div><h1>{fr.events.title}</h1><p className="muted">{fr.events.intro}</p></div>
        {s.can("event.write") && <button className="primary" onClick={() => setCreating(true)}>{fr.events.newEvent}</button>}
      </div>
      <div className="row wrap">
        <span className="muted small">{fr.events.filterStatus} :</span>
        <div className="segmented">
          {(["ALL", "PUBLISHED", "DRAFT", "ARCHIVED"] as const).map((k) => (
            <button key={k} className={filter === k ? "active" : ""} onClick={() => setFilter(k)}>{k === "ALL" ? fr.common.all : fr.events.status[k]}</button>
          ))}
        </div>
        <span className="muted small">{fr.events.filterPhase} :</span>
        <div className="segmented">
          {(["ALL", "ONGOING", "UPCOMING", "ENDED"] as const).map((k) => (
            <button key={k} className={phaseFilter === k ? "active" : ""} onClick={() => setPhaseFilter(k)}>{k === "ALL" ? fr.common.all : fr.events.phase[k]}</button>
          ))}
        </div>
        <input type="search" className="grow" placeholder={fr.events.searchName} aria-label={fr.events.searchName} value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>
      {error && <div className="error">{error}</div>}
      {!events && !error && <p className="muted">{fr.common.loading}</p>}
      {events && events.length === 0 && <p>{fr.events.none}</p>}
      {events && events.length > 0 && shown.length === 0 && <p className="muted">{fr.events.noneFiltered}</p>}
      <div className="cards">
        {shown.map((e) => (
          <div key={e.id} className={"card event-card st-" + e.status}>
            <div className="row between"><h3>{e.name}</h3><span className="row tight"><PhaseBadge phase={phase(e, now)} /><StatusBadge status={e.status} /></span></div>
            <div className="muted small">{formatDateTime(e.startAt)} → {formatDateTime(e.endAt)}</div>
            {e.description && <p className="small clamp">{e.description}</p>}
            <div className="small">{fr.events.restrictions(e.restrictionCount)} · {fr.events.pois(e.poiCount)} · {fr.events.revision} {e.revision}</div>
            <div className="muted small">{fr.events.updated} {formatDateTime(e.updatedAt)}{e.updatedBy ? ` ${fr.common.by} ${e.updatedBy}` : ""}</div>
            {e.pendingReports > 0 && <div className="warn small">{fr.events.pending(e.pendingReports)}</div>}
            <div className="row card-actions">
              <Link className="button primary" to={`/events/${encodeURIComponent(e.id)}`}>{fr.events.open}</Link>
              {s.can("route.test") && <Link className="button" to={`/test?event=${encodeURIComponent(e.id)}`}>{fr.events.test}</Link>}
            </div>
          </div>
        ))}
        {s.can("route.test") && (
          <div className="card dashed">
            <h3>{fr.events.openFile}</h3>
            <p className="muted small">{fr.events.openFileHelp}</p>
            <input type="file" accept=".json,application/json" onChange={(ev) => ev.target.files?.[0] && openFile(ev.target.files[0])} />
            {fileErrors.length > 0 && <div className="error small"><strong>{fr.events.invalid}</strong><ul>{fileErrors.map((x) => <li key={x}>{x}</li>)}</ul></div>}
          </div>
        )}
      </div>
      <CreateEventDialog open={creating} onClose={() => setCreating(false)} onCreated={(h) => navigate(`/events/${h.id}`)} />
    </div>
  );
}

function CreateEventDialog({ open, onClose, onCreated }: { open: boolean; onClose(): void; onCreated(h: EventHeader): void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [key] = useState(newIdempotencyKey);
  const offline = useOffline();
  // default POI categories of the administrator (SW-PO-02, DR19): none ticked, the operator chooses
  const [defaults, setDefaults] = useState<DefaultPoiCategory[] | null>(null);
  useEffect(() => {
    if (!open) return;
    api.GET("/default-categories").then((r) => setDefaults(unwrap(r))).catch(() => setDefaults([]));
  }, [open]);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const togglePicked = (id: string) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const startAt = fromParisInput(start), endAt = fromParisInput(end);
    if (Date.parse(endAt) <= Date.parse(startAt)) { setError(fr.events.endBeforeStart); return; }
    setBusy(true); setError(null);
    try {
      const categories = (defaults ?? []).filter((c) => picked.has(c.id ?? "")).map((c, i) => ({ id: `default-${i}`, name: c.name, icon: c.icon, color: c.color, sortOrder: i, hidden: false }));
      const h = unwrap(await api.POST("/events", { body: { name: name.trim(), description, startAt, endAt, categories }, headers: { "Idempotency-Key": key } }));
      onCreated(h);
    } catch (x) { setError(errorMessage(x, fr.errors)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} title={fr.events.newEvent} onClose={onClose}>
      <form onSubmit={submit} className="stack">
        <Field label={fr.events.name}><input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} autoFocus /></Field>
        <div className="grid2">
          <Field label={fr.events.start}><input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} required /></Field>
          <Field label={fr.events.end}><input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} required /></Field>
        </div>
        <Field label={fr.events.description}><textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        <fieldset className="default-categories">
          <legend>{fr.events.defaultCategories}</legend>
          {defaults === null && <p className="muted small">{fr.common.loading}</p>}
          {defaults?.length === 0 && <p className="muted small">{fr.events.noDefaultCategory}</p>}
          {(defaults?.length ?? 0) > 0 && (
            <>
              <div className="row wrap">
                {defaults!.map((c) => (
                  <label key={c.id} className="check">
                    <input type="checkbox" checked={picked.has(c.id ?? "")} onChange={() => togglePicked(c.id ?? "")} />
                    <span className="kind-dot poi" style={{ background: c.color || undefined }} /> {c.name}
                  </label>
                ))}
              </div>
              <div className="row">
                <button type="button" className="link" onClick={() => setPicked(new Set(defaults!.map((c) => c.id ?? "")))}>{fr.events.pickAll}</button>
                <button type="button" className="link" onClick={() => setPicked(new Set())}>{fr.events.pickNone}</button>
              </div>
            </>
          )}
          <p className="muted small">{fr.events.categoriesLater}</p>
        </fieldset>
        {error && <div className="error">{error}</div>}
        <div className="row end">
          <button type="button" onClick={onClose}>{fr.common.cancel}</button>
          <button className="primary" type="submit" disabled={busy || offline || !name.trim() || !start || !end} title={offline ? fr.common.offlineShort : undefined}>{busy ? fr.common.saving : fr.common.create}</button>
        </div>
      </form>
    </Dialog>
  );
}
